import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Pin, Trash2, PinOff } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ClientNote {
  id: string;
  note_text: string;
  created_by: string;
  created_at: string;
  is_pinned: boolean;
}

interface ClientNotesTabProps {
  clientId: string;
}

export default function ClientNotesTab({ clientId }: ClientNotesTabProps) {
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [noteText, setNoteText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchNotes();
  }, [clientId]);

  const fetchNotes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_notes')
        .select('*')
        .eq('client_id', clientId)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notes',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    setIsSaving(true);
    try {
      const { data: session } = await supabase.auth.getUser();
      if (!session.user) throw new Error('Not authenticated');

      const { error } = await supabase.from('client_notes').insert({
        client_id: clientId,
        note_text: noteText.trim(),
        created_by: session.user.id,
      });

      if (error) throw error;

      setNoteText('');
      fetchNotes();
      toast({
        title: 'Note added',
        description: 'Your note has been saved successfully',
      });
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePinNote = async (noteId: string, currentPinned: boolean) => {
    try {
      const { error } = await supabase
        .from('client_notes')
        .update({ is_pinned: !currentPinned })
        .eq('id', noteId);

      if (error) throw error;

      fetchNotes();
      toast({
        title: currentPinned ? 'Note unpinned' : 'Note pinned',
      });
    } catch (error) {
      console.error('Error pinning note:', error);
      toast({
        title: 'Error',
        description: 'Failed to update note',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      const { error } = await supabase
        .from('client_notes')
        .delete()
        .eq('id', noteToDelete);

      if (error) throw error;

      fetchNotes();
      toast({
        title: 'Note deleted',
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete note',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
    }
  };

  const pinnedNotes = notes.filter(n => n.is_pinned);
  const regularNotes = notes.filter(n => !n.is_pinned);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label htmlFor="new_note">New Note</Label>
        <Textarea
          id="new_note"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Type note here..."
          rows={4}
          maxLength={2000}
        />
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {noteText.length}/2000 characters
          </span>
          <Button onClick={handleAddNote} disabled={isSaving || !noteText.trim()}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Note
          </Button>
        </div>
      </div>

      <div className="border-t pt-6">
        <h4 className="font-medium mb-4">Previous Notes</h4>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No notes yet
          </p>
        ) : (
          <div className="space-y-4">
            {pinnedNotes.length > 0 && (
              <div className="space-y-3">
                {pinnedNotes.map((note) => (
                  <div
                    key={note.id}
                    className="p-4 border rounded-md bg-accent/50"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Pin className="h-4 w-4 fill-current" />
                        <span title={new Date(note.created_at).toLocaleString()}>
                          {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePinNote(note.id, note.is_pinned)}
                        >
                          <PinOff className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setNoteToDelete(note.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{note.note_text}</p>
                  </div>
                ))}
              </div>
            )}

            {regularNotes.length > 0 && (
              <div className="space-y-3">
                {regularNotes.map((note) => (
                  <div
                    key={note.id}
                    className="p-4 border rounded-md"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-sm text-muted-foreground" title={new Date(note.created_at).toLocaleString()}>
                        {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePinNote(note.id, note.is_pinned)}
                        >
                          <Pin className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setNoteToDelete(note.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{note.note_text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteNote}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
